<template>
    <cr-dropdown data-testing="profile-menu">
        <cr-dropdown-toggle :label="t('PROFILE_MENU_LABEL')" text class="profile-menu-toggle" data-testing="profile-menu-toggle">
            <span class="profile-menu-toggle-icon">
                <cr-avatar avatar="bi-person-fill" data-testing="profile-menu-avatar"/>
            </span>
        </cr-dropdown-toggle>
        <cr-dropdown-list class="profile-menu-list">
            <cr-dropdown-list-item tag="div" class="profile-menu-user-name" data-testing="profile-menu-user-name">
                <b>{{ userName }}</b>
            </cr-dropdown-list-item>

            <cr-dropdown-list-divider/>

            <cr-dropdown-list-item @click="onViewReportCardClicked" class="cr-mb2" data-testing="profile-menu-view-report-card">
                {{ t('PROFILE_MENU_VIEW_REPORT_CARD') }}
            </cr-dropdown-list-item>
            <cr-dropdown-list-item tag="div" data-testing="profile-menu-view-courseware">
                <router-link class="profile-menu-link" :to="Route.SsoCourseware.path" target="_blank" rel="noopener">
                    {{ t('PROFILE_MENU_VIEW_COURSEWARE') }}
                    <cr-icon class="cr-external-link-icon cr-ml2" :alt="t('PROFILE_MENU_OPENS_IN_NEW_TAB')">bi-box-arrow-up-right</cr-icon>
                </router-link>
            </cr-dropdown-list-item>

            <cr-dropdown-list-divider/>

            <cr-dropdown-list-item @click="onResetProgressClicked" data-testing="profile-menu-reset-progress">
                {{ t('PROFILE_MENU_RESET_PROGRESS') }}
            </cr-dropdown-list-item>

            <cr-dropdown-list-divider/>

            <cr-dropdown-list-item tag="div" class="cr-mb2" data-testing="profile-menu-knowledge-base">
                <cr-external-link :href="kbUrl">{{ t('PROFILE_MENU_KNOWLEDGE_BASE') }}</cr-external-link>
            </cr-dropdown-list-item>
            <cr-dropdown-list-item tag="div" class="cr-mb2" data-testing="profile-menu-contact-support">
                <router-link class="profile-menu-link" :to="Route.SsoContactSupport.path" target="_blank" rel="noopener">
                    {{ t('PROFILE_MENU_CONTACT_SUPPORT') }}
                    <cr-icon class="cr-external-link-icon cr-ml2" :alt="t('PROFILE_MENU_OPENS_IN_NEW_TAB')">bi-box-arrow-up-right</cr-icon>
                </router-link>
            </cr-dropdown-list-item>

            <cr-dropdown-list-item @click="onLogout" data-testing="profile-menu-logout">
                {{ t('PROFILE_MENU_LOGOUT') }}
            </cr-dropdown-list-item>
        </cr-dropdown-list>
    </cr-dropdown>
    <reset-progress-dialog v-model="showResetProgressDialog" @confirm="onResetProgressComplete" @cancel="onResetProgressComplete"/>
    <report-card-dialog v-model="showReportCardDialog"/>
</template>

<style scoped>
.profile-menu-toggle
{
    border-radius: 50%;
    padding: 0.25rem;
    aspect-ratio: 1;
}

/* Single ring for profile toggle */
.profile-menu-toggle:focus-visible
{
    box-shadow: 0 0 0 2px var(--cr-input-focus-box-shadow-color);
}

.profile-menu-toggle-icon :deep(.cr-avatar)
{
    width: 2.5rem;
    height: 2.5rem;
    background-color: var(--cr-profile-menu-background);
    color: var(--cr-profile-menu-text);
    border: 2px solid var(--cr-profile-menu-accent);
}

.profile-menu-toggle-icon :deep(.cr-avatar .cr-icon)
{
    /* In between small and medium */
    font-size: 1.4rem;
}

.profile-menu-list
{
    min-width: 250px;
    background-color: var(--cr-profile-menu-background);
    border: 1px solid var(--cr-profile-menu-border);
    --cr-menu-hover-background-color: var(--cr-profile-menu-border);
    --cr-menu-hover-text-color: inherit;
}

.profile-menu-list :deep(.cr-dropdown-list-item)
{
    color: var(--cr-profile-menu-accent);
}

.profile-menu-list :deep(.cr-external-link),
.profile-menu-list .profile-menu-link
{
    color: inherit;
    text-decoration: none;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
}

/* Ring around the whole item, not the inner link */
.profile-menu-list .profile-menu-link:focus-visible,
.profile-menu-list :deep(.cr-external-link:focus-visible)
{
    box-shadow: none;
    outline: none;
}
.profile-menu-list :deep(.cr-dropdown-list-item:has(:focus-visible))
{
    box-shadow: inset 0 0 0 0.15rem var(--cr-input-focus-box-shadow-color);
}

.profile-menu-list :deep(.cr-dropdown-list-divider)
{
    border-color: var(--cr-profile-menu-border);
}

.profile-menu-list :deep(.profile-menu-user-name),
.profile-menu-list :deep(.profile-menu-user-name) *
{
    color: var(--cr-profile-menu-text);
}
</style>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSessionStore } from '../stores/sessionStore';
import Config from '../config';
import Route from '../routers/route';
import ResetProgressDialog from './dialogs/ResetProgressDialog.vue';
import ReportCardDialog from './dialogs/ReportCardDialog.vue';

const { t } = useI18n();
const sessionStore = useSessionStore();

const userName = computed(() => sessionStore.identity?.name ?? '');
const kbUrl = Config.KB_BASE_URL;

const showResetProgressDialog = ref(false);
const showReportCardDialog = ref(false);

function onViewReportCardClicked()
{
    showReportCardDialog.value = true;
}

function onResetProgressClicked()
{
    showResetProgressDialog.value = true;
}

function onResetProgressComplete()
{
    showResetProgressDialog.value = false;
}

function onLogout()
{
    sessionStore.logout();
}
</script>
